import { APP_CURRENCY, formatMoneyCents } from "@/lib/currency";

/**
 * Coach AI subscription tiers.
 *
 * Each tier has a flat monthly price and a discounted annual price. Both
 * cycles start with a seven day free trial, and Pro (monthly or annual) is the
 * only tier that unlocks the IFBB Posing Coach.
 */
export type Tier = "standard" | "pro";
export type BillingCycle = "monthly" | "yearly";

export interface TierCycleDef {
  /** Stripe price lookup key — same value in test and live. */
  priceId: string;
  /** Amount in cents (AUD) charged each interval. */
  unitAmount: number;
  /** Display-only original price shown struck through. */
  compareAtAmount?: number;
}

export interface TierDef {
  id: Tier;
  name: string;
  blurb: string;
  perks: string[];
  monthly: TierCycleDef;
  yearly: TierCycleDef;
}

export const TIERS: Record<Tier, TierDef> = {
  standard: {
    id: "standard",
    name: "Standard",
    blurb: "Meal plans, training plans and AI chat coaching. Flat pricing, forever.",
    perks: [
      "AI meal plans built around your goal",
      "AI training plans and progressions",
      "AI chat coaching, anytime",
      "Flat price — never goes up",
    ],
    monthly: { priceId: "standard", unitAmount: 1500 },
    yearly: { priceId: "standard_yearly", unitAmount: 14900 },
  },
  pro: {
    id: "pro",
    name: "Pro",
    blurb: "Everything in Standard plus the full IFBB Posing Coach.",
    perks: [
      "Everything in Standard",
      "IFBB Posing Coach — full pose library",
      "Posing video analysis with AI feedback",
      "Division specific judging rubrics",
      "Measured joint angle feedback",
      "Flat price — never goes up",
    ],
    monthly: { priceId: "pro", unitAmount: 2700, compareAtAmount: 4900 },
    yearly: { priceId: "pro_yearly", unitAmount: 22900 },
  },
};

export const PRO_FEATURE_LABEL = "the IFBB Posing Coach";

/** Every Stripe lookup key the app is allowed to check out with. */
export const ALL_TIER_PRICE_IDS: string[] = (["standard", "pro"] as Tier[]).flatMap(
  (t) => [TIERS[t].monthly.priceId, TIERS[t].yearly.priceId],
);

export function tierCycle(tier: Tier, cycle: BillingCycle): TierCycleDef {
  return cycle === "yearly" ? TIERS[tier].yearly : TIERS[tier].monthly;
}

export function priceIdFor(tier: Tier, cycle: BillingCycle): string {
  return tierCycle(tier, cycle).priceId;
}

/**
 * Whole-number percentage saved by paying annually instead of twelve months at
 * the monthly rate. Rounded down so the "save over N%" copy is never a lie.
 */
export function annualSavingsPercent(tier: Tier): number {
  const twelveMonths = TIERS[tier].monthly.unitAmount * 12;
  const annual = TIERS[tier].yearly.unitAmount;
  if (twelveMonths <= 0 || annual >= twelveMonths) return 0;
  return Math.floor(((twelveMonths - annual) / twelveMonths) * 100);
}

/** e.g. "Save over 17% — $149/year instead of $180". */
export function annualSavingsLabel(tier: Tier): string {
  const pct = annualSavingsPercent(tier);
  const twelveMonths = formatTierPrice(TIERS[tier].monthly.unitAmount * 12);
  const annual = formatTierPrice(TIERS[tier].yearly.unitAmount);
  return `Save over ${pct}% — ${annual}/year instead of ${twelveMonths}`;
}

/** Annual price expressed as an effective monthly amount, in cents. */
export function effectiveMonthlyAmount(tier: Tier): number {
  return Math.round(TIERS[tier].yearly.unitAmount / 12);
}

/**
 * Maps a stored `subscriptions.price_id` to a tier.
 *
 * New subscriptions store the Stripe price lookup key ("standard",
 * "standard_yearly", "pro", "pro_yearly"). Legacy rows store the old
 * "monthly"/"yearly" keys or a raw `price_…` ID — those subscribers paid the
 * original full price, so they keep Pro access.
 */
export function tierFromPriceId(priceId?: string | null): Tier | null {
  if (!priceId) return null;
  const key = priceId.toLowerCase();
  if (key === "standard" || key === "standard_yearly") return "standard";
  if (key === "pro" || key === "pro_yearly") return "pro";
  if (key === "pending") return null;
  // Legacy / grandfathered plans.
  return "pro";
}

/** Billing cycle of a stored price_id, when it can be determined. */
export function cycleFromPriceId(priceId?: string | null): BillingCycle | null {
  if (!priceId) return null;
  const key = priceId.toLowerCase();
  if (key === "standard_yearly" || key === "pro_yearly" || key === "yearly") {
    return "yearly";
  }
  if (key === "standard" || key === "pro" || key === "monthly") return "monthly";
  return null;
}

/** Formats cents as an AUD string, e.g. 2700 → "A$27". */
export function formatTierPrice(cents: number, currency = APP_CURRENCY): string {
  return formatMoneyCents(cents, currency);
}
